<script lang="ts" setup>
import { ComponentPublicInstance, computed, onBeforeMount, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import { BModal } from 'bootstrap-vue';
import { groupBy } from 'lodash';

import * as optionSetsApi from '@/api/shopify/optionSets';

import { OptionSetDto } from '@extTypes/options/optionSet';
import { StoreType } from '@extTypes/api/customily/user';
import { CustomilyTemplateDto } from '@extTypes/api/customily/template';
import { AuditEntity } from '@extTypes/auditLog/types';
import { LinkedOptionSet } from '@extTypes/enums/option-sets';
import { EntityVersionRestored } from '@extTypes/versioning';

import config from '@/config/config';
import { useStore } from '@/store';
import { useEventListener } from '@/composables/eventListener';
import { useTableRowsPerPage } from '@/composables/useTableRowsPerPage';

import * as storeUtil from '@/utils/stores';
import { DateFormat, formatDate } from '@/utils/dates';
import { useModal } from '@/utils/bootstrapVueUtils';
import { linkedOptionSets } from '@/utils/constants/option-sets';

import { FieldType, TableField } from '@/typings/dynamicTable';

import Loader from '@/components/Loader/Loader.vue';
import CustomilyProductsList from '@/components/CustomilyProductsList/CustomilyProductsList.vue';
import AuditLog from '@/components/AuditLog/AuditLog.vue';
import SetProgression from '@/components/Set/SetProgression/SetProgression.vue';
import CustomCheckbox from '@/components/shared/CustomCheckbox.vue';
import CButton from '@/components/shared/CButton/CButton.vue';
import DynamicTable from '@/components/shared/DynamicTable.vue';
import CInput from '@/components/shared/CInput.vue';
import EditableText from '@/components/EditableText/EditableText.vue';
import OptionSetLinkedToProductsModal from './OptionSetLinkedToProductsModal/OptionSetLinkedToProductsModal.vue';

interface ProductUsingSet {
    title: string;
    id: string;
    isDesign: boolean;
}

interface SetUsedByProducts {
    setId: string;
    setName: string;
    products: ProductUsingSet[];
}

const CURRENT_PAGE_STORAGE_KEY = 'customily-current-sets-page';
const FILTERS_STORAGE_KEY = 'lastSessionOptionSetsFilters';
const FALLBACK_SETS_PER_PAGE = 10;
const LOADING_SETS_MESSAGE = 'Loading sets';
const EXPORTING_SETS_MESSAGE = 'Exporting sets';
const DELETE_CONFIRM_OPTIONS = {
    centered: true,
    noCloseOnBackdrop: true,
    noCloseOnEsc: true,
    okTitle: 'Delete',
    okVariant: 'danger',
    footerClass: 'justify-content-between'
};
const BETWEEN_DOWNLOADS_MS = 500;

const readStoredPage = (): number => {
    const storedPage = localStorage.getItem(CURRENT_PAGE_STORAGE_KEY);
    return (storedPage && Number.parseInt(storedPage, 10)) || 1;
};

const readStoredFilter = (): LinkedOptionSet => {
    try {
        const filters = JSON.parse(sessionStorage.getItem(FILTERS_STORAGE_KEY) || '{}');
        return filters.onlyLinkedOptionSets || LinkedOptionSet.All;
    } catch {
        return LinkedOptionSet.All;
    }
};

const { state, commit } = useStore();
const bvModal = useModal();
const router = useRouter();
const route = useRoute();

// The route guards already ensure there is an authenticated user before this view renders
const user = computed(() => state.user!);
const shop = computed(() => state.store);
const forDesignTemplatesMode = computed(() => state.forDesignTemplatesMode);

const optionSets = ref<OptionSetDto[]>([]);
const selectedSets = ref<OptionSetDto[]>([]);
const selectedSet = ref<OptionSetDto | null>(null);
const selectedOptionSet = ref<OptionSetDto | undefined>();
const productsUsingDeletedSet = ref<ProductUsingSet[]>([]);
const productsUsingDeletedSets = ref<SetUsedByProducts[]>([]);
const optionSetsZipFile = ref<File | null>(null);
const setTaskIdentifier = ref('');
const copiedId = ref('');

const showLoader = ref(false);
const loaderMessage = ref(LOADING_SETS_MESSAGE);
const pageLoaded = ref(false);
const showAuditLogModal = ref(false);
const showOptionSetLinkedToProductsModal = ref(false);

const sortBy = ref('updated_at');
const sortMode = ref<'ASC' | 'DESC'>('DESC');
const onlyLinkedOptionSets = ref<LinkedOptionSet>(readStoredFilter());
const entityType = AuditEntity.OPTION_SET;

// Seeded from `localStorage` before the page watcher below is registered, so restoring the last
// visited page does not count as a page change. `pageSize` is replaced by the measured one before
// the first fetch.
const pagination = reactive({
    searchValue: '',
    currentPage: readStoredPage(),
    pageSize: FALLBACK_SETS_PER_PAGE,
    totalItemCount: 0
});

const setsList = ref<ComponentPublicInstance>();
const setInUseModal = ref<BModal>();
const uploadFileModal = ref<BModal>();
const templatePickerModal = ref<BModal>();
const optionSetsLinkedToProductsModal = ref<BModal>();

const isManaged = computed(() => user.value.customilyUser.IsManaged);
const isCustomStore = computed(() => storeUtil.isCustomStore(shop.value, user.value.customilyUser.Stores));
const isAllChecked = computed(() => selectedSets.value.length > 0);
const optionSetsFiltersLabel = computed(
    () => linkedOptionSets.find((option) => option.value === onlyLinkedOptionSets.value)?.text ?? 'All option sets'
);

const tableConfig: TableField[] = [
    {
        key: 'check',
        label: '',
        thStyle: { width: '48px', padding: '0 0.75rem 0.75rem 0.75rem' }
    },
    {
        key: 'title',
        label: 'Option set',
        type: FieldType.Sort,
        sortKey: 'optionset_name',
        thStyle: { padding: '0 0.75rem 0.75rem 0.75rem' }
    },
    {
        key: 'created_at',
        label: 'Created',
        formatter: (value) => formatDate(value, DateFormat.MMMddYYYYTime),
        thStyle: { width: '180px' },
        type: FieldType.Sort,
        sortKey: 'created_at'
    },
    {
        key: 'updated_at',
        label: 'Last Modified',
        formatter: (value) => formatDate(value, DateFormat.MMMddYYYYTime),
        thStyle: { width: '180px', padding: '0 0.75rem 0.75rem 0.75rem' },
        type: FieldType.Sort,
        sortKey: 'updated_at'
    },
    {
        key: 'linked',
        label: 'Linked to Product',
        thStyle: { width: '145px', padding: '0 0.75rem 0.75rem 0.75rem' }
    },
    { key: 'actions', label: '', thStyle: { width: '250px', padding: '0 0.75rem 0.75rem 0.75rem' } }
];

const loadSets = async () => {
    try {
        showLoader.value = true;
        optionSets.value = await optionSetsApi.getOptionSets(
            pagination.searchValue,
            (pagination.currentPage - 1) * pagination.pageSize,
            pagination.pageSize,
            forDesignTemplatesMode.value,
            onlyLinkedOptionSets.value,
            undefined,
            sortBy.value,
            sortMode.value
        );
    } catch (error) {
        window.captureException(error as Error);
    } finally {
        showLoader.value = false;
    }
};

const getSetsCount = async () => {
    try {
        const { count } = await optionSetsApi.getOptionSetsCount(
            pagination.searchValue,
            forDesignTemplatesMode.value,
            onlyLinkedOptionSets.value
        );
        pagination.totalItemCount = count;
    } catch (error) {
        window.captureException(error as Error);
    }
};

/** Reloads the list and the total count for the current query. */
const refreshSets = async () => {
    await Promise.all([getSetsCount(), loadSets()]);
};

/**
 * Set while the page is changed from our own code, which already reloads the list on its own.
 * Without it, resetting to the first page would fire a second request for the same list.
 */
let skipNextPageReload = false;

const goToFirstPage = () => {
    if (pagination.currentPage === 1) return;
    skipNextPageReload = true;
    pagination.currentPage = 1;
};

watch(
    () => pagination.currentPage,
    (page) => {
        localStorage.setItem(CURRENT_PAGE_STORAGE_KEY, page.toString());

        if (skipNextPageReload) {
            skipNextPageReload = false;
            return;
        }

        loadSets();
    }
);

/** Reloads from the first page. Search, sort and filter changes all go through here. */
const applyQueryChange = async () => {
    goToFirstPage();
    await refreshSets();
};

watch(() => pagination.searchValue, applyQueryChange);

const { whenMeasured } = useTableRowsPerPage({
    anchor: setsList,
    rowHeight: 85,
    reservedHeight: 180,
    fallbackRows: FALLBACK_SETS_PER_PAGE,
    onChange: (rowsPerPage) => {
        pagination.pageSize = rowsPerPage;
        applyQueryChange();
    }
});

const sortSets = (sortKey?: string) => {
    if (!sortKey) return;

    sortBy.value = sortKey;
    sortMode.value = 'DESC';
    applyQueryChange();
};

const sortModeSets = (event: MouseEvent) => {
    event.stopPropagation();
    sortMode.value = sortMode.value === 'DESC' ? 'ASC' : 'DESC';
    applyQueryChange();
};

const filterOptionSets = async (filter = LinkedOptionSet.All) => {
    onlyLinkedOptionSets.value = filter;
    sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify({ onlyLinkedOptionSets: filter }));
    await applyQueryChange();
};

const isSetIdSelected = (setId: string) => selectedSets.value.some((s) => s.id === setId);

const toggleSetSelection = (set: OptionSetDto) => {
    // Compared by id because a reload replaces the objects backing the same sets
    selectedSets.value = isSetIdSelected(set.id)
        ? selectedSets.value.filter((s) => s.id !== set.id)
        : [...selectedSets.value, set];
};

const checkAll = () => {
    selectedSets.value = isAllChecked.value ? [] : [...optionSets.value];
};

const handleOptionSetsBulkDelete = async () => {
    const count = selectedSets.value.length;
    const confirmed = await bvModal?.msgBoxConfirm(
        `Are you sure you want to delete ${count} Option ${count === 1 ? 'Set' : 'Sets'}?`,
        { ...DELETE_CONFIRM_OPTIONS, title: 'Delete Option Set(s)' }
    );
    if (!confirmed) return;

    try {
        showLoader.value = true;

        const linkedSets = selectedSets.value.filter((set) => set.linked);
        productsUsingDeletedSets.value = await Promise.all(
            linkedSets.map(async (set) => ({
                setId: set.id,
                setName: set.title,
                products: await optionSetsApi.getProductsWithSet(set.id)
            }))
        );

        const unlinkedOptionSetIds = selectedSets.value.filter((set) => !set.linked).map((set) => set.id);

        // Sets in use cannot be deleted, the user is shown which products still use them
        if (!unlinkedOptionSetIds.length) {
            showLoader.value = false;
            optionSetsLinkedToProductsModal.value?.show();
            return;
        }

        await optionSetsApi.bulkDeleteOptionsSets(unlinkedOptionSetIds);
        await refreshSets();
        selectedSets.value = selectedSets.value.filter((set) => set.linked);
    } catch (error) {
        showLoader.value = false;
        window.captureException(error as Error);
    }
};

const removeSet = async (setToDelete: OptionSetDto) => {
    const confirmed = await bvModal?.msgBoxConfirm(
        `Are you sure you want to delete the "${setToDelete.title}" option set?`,
        { ...DELETE_CONFIRM_OPTIONS, title: 'Delete set' }
    );
    if (!confirmed) return;

    try {
        showLoader.value = true;
        productsUsingDeletedSet.value = await optionSetsApi.getProductsWithSet(setToDelete.id);

        // Don't remove the set if it's in use
        if (productsUsingDeletedSet.value.length) {
            showLoader.value = false;
            setInUseModal.value?.show();
            return;
        }

        await optionSetsApi.deleteSet(setToDelete);
        await refreshSets();
    } catch (error) {
        showLoader.value = false;
        window.captureException(error as Error);
    }
};

const cloneOptionSet = async (set: OptionSetDto) => {
    try {
        showLoader.value = true;
        await optionSetsApi.cloneOptionSet(set, forDesignTemplatesMode.value);
        await refreshSets();
    } catch (error) {
        showLoader.value = false;
        window.captureException(error as Error);
    }
};

const auditOptionSet = (set: OptionSetDto) => {
    selectedSet.value = set;
    showAuditLogModal.value = true;
};

const onAuditClose = () => {
    selectedSet.value = null;
    showAuditLogModal.value = false;
};

const addOptionSet = () => {
    try {
        router.push({ name: 'CreateOptionSet' });
    } catch (error) {
        window.captureException(error as Error);
    }
};

const editOptionSet = (setId: string | number) => {
    try {
        router.push({
            name: 'Set',
            query: {
                set: setId.toString()
            }
        });
    } catch (error) {
        window.captureException(error as Error);
    }
};

const handleClickRow = (set: unknown) => editOptionSet((set as OptionSetDto).id);

const openInNewTab = (event: MouseEvent, setId: string) => {
    event.stopPropagation();
    try {
        window.open(`${config.CUSTOMILY_BASE_URL}/store/set/?set=${setId}`);
    } catch (error) {
        window.captureException(error as Error);
    }
};

const copyId = (event: MouseEvent, id: string) => {
    event.stopPropagation();

    navigator.clipboard
        .writeText(id.toString())
        .then(() => {
            copiedId.value = id;
            setTimeout(() => {
                copiedId.value = '';
            }, 1000);
        })
        .catch((error) => {
            console.error('Error trying to copy id', error);
        });
};

const downloadOptionSetZips = (urls: string[]) => {
    const downloadNext = (index: number) => {
        if (index >= urls.length) return;

        const link = document.createElement('a');
        link.href = urls[index];
        link.target = '_parent';

        // The link has to be in the document for the click to work
        (document.body || document.documentElement).append(link);
        link.click();
        link.remove();

        // Download the next file with a small timeout
        setTimeout(() => downloadNext(index + 1), BETWEEN_DOWNLOADS_MS);
    };

    downloadNext(0);
};

const exportOptionSets = async () => {
    try {
        loaderMessage.value = EXPORTING_SETS_MESSAGE;
        showLoader.value = true;

        // One export per store, since the selection can span several of them
        const shopGroups = groupBy(selectedSets.value, (set) => set.shop);
        const responses = await Promise.all(
            Object.entries(shopGroups)
                .filter(([, sets]) => sets.length > 0)
                .map(([shopUrl, sets]) => optionSetsApi.exportOptionSets(shopUrl, sets.map((s) => s.id).join('_')))
        );

        downloadOptionSetZips(responses.map((response) => response.exportUrl));
    } catch (error) {
        window.captureException(error as Error);
    } finally {
        showLoader.value = false;
        loaderMessage.value = LOADING_SETS_MESSAGE;
    }
};

const showUploadFileModal = () => uploadFileModal.value?.show();

const closeUploadFileModal = () => uploadFileModal.value?.hide();

const importOptionSets = async () => {
    const targetStore = [...user.value.customilyUser.Stores]
        .sort((a, b) => a.Id - b.Id)
        .find((s) => s.Type === StoreType.Shopify || s.Type === StoreType.WooCommerce);

    if (!targetStore || !optionSetsZipFile.value) {
        console.error('Please select .zip file with options to import.');
        return;
    }

    try {
        uploadFileModal.value?.hide();
        showLoader.value = true;
        await optionSetsApi.importOptionSets(targetStore.Url, optionSetsZipFile.value);

        // The imported sets are the most recently updated ones, show them first
        sortBy.value = 'updated_at';
        sortMode.value = 'DESC';
        await applyQueryChange();
    } catch (error) {
        showLoader.value = false;
        window.captureException(error as Error);
    }
};

const showTemplateSelectionModal = () => templatePickerModal.value?.show();

const onTemplateSelect = async (data: { template: CustomilyTemplateDto }) => {
    const result = await optionSetsApi.createSetFromTemplate(
        data.template.id.trim().toLowerCase(),
        user.value.customilyUser.IsManaged,
        `${data.template.name} (option set)`,
        '',
        shop.value
    );
    setTaskIdentifier.value = result.taskIdentifier;
};

const onTemplatePickerHidden = () => {
    setTaskIdentifier.value = '';
    router.go(0);
};

const openOptionSetLinkedToProductsModal = (item: OptionSetDto) => {
    selectedOptionSet.value = item;
    showOptionSetLinkedToProductsModal.value = true;
};

const closeOptionSetLinkedToProductsModal = () => {
    selectedOptionSet.value = undefined;
    showOptionSetLinkedToProductsModal.value = false;
};

const onOptionSetRename = async (id: string, newTitle: string) => {
    const set = optionSets.value.find((s) => s.id === id);
    if (!set) return;

    const title = String(newTitle).trim();
    if (!title || title === set.title) return;

    const previousTitle = set.title;
    set.title = title;

    try {
        await optionSetsApi.renameOptionSet(id, title);
    } catch (error) {
        set.title = previousTitle;
        window.captureException(error as Error);
    }
};

const onOptionSetVersionRestored = async (restoredVersion: EntityVersionRestored) => {
    const restoredOptionSet = restoredVersion.data as OptionSetDto;

    try {
        showLoader.value = true;
        const title = `${restoredOptionSet.title} (Restored)`;
        const setId = await optionSetsApi.addOptionSet(
            {
                title,
                options: restoredOptionSet.options
            },
            restoredOptionSet.forDesignTemplate
        );
        await optionSetsApi.saveOptionSet({
            id: setId,
            title,
            options: restoredOptionSet.options,
            sections: [],
            shop: restoredOptionSet.shop,
            linked: false,
            forDesignTemplate: restoredOptionSet.forDesignTemplate,
            clientId: restoredOptionSet.clientId,
            libraryId: restoredOptionSet.libraryId,
            createdFromTemplate: null
        });
        await refreshSets();
    } catch (error) {
        showLoader.value = false;
        window.captureException(error as Error);
    }
};

// Refresh requested by the legacy app embedding this view
useEventListener('message', (event) => {
    if ((event as MessageEvent).data?.action === 'refresh-sets-list') refreshSets();
});

onBeforeMount(async () => {
    if (user.value.customilyUser.AccessRoles.Serialized.includes('admin')) {
        commit('setDesignTemplatesMode', !!route.query['forDesignTemplates']);
    }

    // The list is only fetched once the real page size is known, so the first request already
    // asks for the rows that fit on screen
    pagination.pageSize = await whenMeasured();

    await refreshSets();
    pageLoaded.value = true;
});
</script>

<template>
    <div class="container-fluid pb-0">
        <Loader :message="loaderMessage" :show="showLoader" />

        <b-form inline class="fixed-content action-btn-container" @submit.prevent>
            <div>
                <c-button
                    v-if="!isManaged"
                    class="ml-2 add-opt-set-btn"
                    text="New Option Set"
                    variant="primary"
                    @click="addOptionSet()"
                />

                <c-button class="ml-2" text="Import Sets" variant="outline-primary" @click="showUploadFileModal()" />
            </div>

            <div class="d-flex">
                <b-dropdown :text="optionSetsFiltersLabel" class="ml-3" variant="outline-primary">
                    <b-dropdown-item
                        v-for="(option, idx) in linkedOptionSets"
                        :key="`option-${idx}`"
                        @click="filterOptionSets(option.value)"
                    >
                        <b-form-radio v-model="onlyLinkedOptionSets" :value="option.value">
                            {{ option.text }}
                        </b-form-radio>
                    </b-dropdown-item>
                </b-dropdown>
                <c-input
                    v-model="pagination.searchValue"
                    type="search"
                    placeholder="Search"
                    class="ml-3"
                    :debounce="400"
                />
            </div>
        </b-form>

        <b-list-group ref="setsList" class="mb-3">
            <div v-if="selectedSets.length" class="text-cell export-row">
                <div>
                    {{ selectedSets.length }} option set(s) selected

                    <c-button
                        v-tippy="{ arrow: true }"
                        class="icons-on-action-col color-purple-dark"
                        size="icon"
                        content="Export option set(s)"
                        :icon="['fa-solid', 'fa-file-export']"
                        dd-name="bulk-export-option-sets"
                        @click="exportOptionSets()"
                    />
                    <c-button
                        v-tippy="{ arrow: true }"
                        class="icons-on-action-col color-purple-dark"
                        size="icon"
                        content="Delete option set(s)"
                        dd-name="bulk-delete-option-sets"
                        :icon="['fa-solid', 'fa-trash']"
                        @click="handleOptionSetsBulkDelete()"
                    />
                </div>
            </div>
            <dynamic-table
                :table-config="tableConfig"
                :pagination-data="pagination"
                :items="optionSets"
                :loading="false"
                :on-row-clicked="handleClickRow"
                :custom-search-and-pagination="true"
                t-body-tr-class="table-row"
                :sort="{ sortBy, sortMode }"
                @sort-items="sortSets"
                @change-sort-mode="sortModeSets"
            >
                <template #head(check)>
                    <div class="d-flex check-col">
                        <custom-checkbox :value="isAllChecked" @click-checkbox="checkAll" />
                    </div>
                </template>
                <template #cell(check)="row">
                    <div class="d-flex check-cell" @click="$event.stopPropagation()">
                        <custom-checkbox
                            :value="isSetIdSelected(row.item.id)"
                            @click-checkbox="toggleSetSelection(row.item)"
                        />
                    </div>
                </template>
                <template #cell(title)="row">
                    <div>
                        <div class="w-100 d-flex">
                            <editable-text
                                :value="row.item.title"
                                :allow-editing="true"
                                class="text-cell w-100 center-icons-text"
                                tooltip="Rename"
                                tooltip-direction="right-end"
                                type="text"
                                @input="onOptionSetRename(row.item.id, $event)"
                                @click.native.stop
                            />
                        </div>
                        <span class="id-container-cell">
                            <span class="id-cell center-icons-text"
                                >{{ row.item.id }}
                                <c-button
                                    :id="`set-copy-id-btn-${row.item.id}`"
                                    v-tippy="{ arrow: true, hideOnClick: false }"
                                    class="table-icons icons-on-action-col color-purple-dark"
                                    size="icon"
                                    :content="copiedId === row.item.id ? 'Copied' : 'Copy'"
                                    :icon="['fas', 'copy']"
                                    @click="copyId($event, row.item.id)"
                                />
                                <c-button
                                    :id="`set-go-new-page-btn-${row.item.id}`"
                                    v-tippy="{ arrow: true }"
                                    class="btn-dark-transparent table-icons icons-on-action-col color-purple-dark"
                                    content="Open in new tab"
                                    :icon="['fa-solid', 'fa-up-right-from-square']"
                                    size="icon"
                                    data-dd-action-name="Open in new tab"
                                    @click="openInNewTab($event, row.item.id)"
                                />
                            </span>
                        </span>
                    </div>
                </template>
                <template #cell(linked)="{ item }">
                    <span class="d-flex justify-content-center">
                        <c-button
                            v-if="item.linked"
                            v-tippy="{ arrow: true }"
                            class="color-purple"
                            size="sm"
                            :icon="['fas', 'circle-check']"
                            content="View detail"
                            data-dd-action-name="View option set detail"
                            @click="openOptionSetLinkedToProductsModal(item)"
                        />
                    </span>
                </template>
                <template #cell(actions)="row">
                    <span @click="$event.stopPropagation()">
                        <c-button
                            :id="`set-history-btn-${row.item.id}`"
                            v-tippy="{ arrow: true }"
                            class="btn-dark-transparent table-icons icons-on-action-col color-purple-dark"
                            size="sm"
                            content="History"
                            :icon="['fas', 'fa-history']"
                            @click="auditOptionSet(row.item)"
                        />
                        <c-button
                            :id="`set-edit-row-btn-${row.item.id}`"
                            v-tippy="{ arrow: true }"
                            class="btn-dark-transparent table-icons icons-on-action-col color-purple-dark"
                            size="sm"
                            content="Edit"
                            :icon="['fas', 'fa-pen']"
                            @click="editOptionSet(row.item.id)"
                        />
                        <c-button
                            :id="`set-clone-btn-${row.item.id}`"
                            v-tippy="{ arrow: true }"
                            class="btn-dark-transparent table-icons icons-on-action-col color-purple-dark"
                            size="sm"
                            content="Clone"
                            :icon="['fas', 'fa-clone']"
                            @click="cloneOptionSet(row.item)"
                        />
                        <c-button
                            :id="`set-delete-btn-${row.item.id}`"
                            v-tippy="{ arrow: true }"
                            class="btn-dark-transparent table-icons icons-on-action-col color-purple-dark"
                            size="sm"
                            content="Delete"
                            :icon="['fas', 'fa-trash']"
                            @click="removeSet(row.item)"
                        />
                    </span>
                </template>
            </dynamic-table>
        </b-list-group>
        <div class="w-100 d-flex justify-content-center">
            <span v-if="pageLoaded">
                <b-pagination
                    v-model="pagination.currentPage"
                    size="md"
                    :total-rows="pagination.totalItemCount"
                    :per-page="pagination.pageSize"
                />
            </span>
        </div>

        <div v-if="isCustomStore">
            <b-btn class="ml-2" variant="primary" @click="showTemplateSelectionModal()">
                + New Option Set From Template
            </b-btn>
        </div>

        <option-set-linked-to-products-modal
            :show="showOptionSetLinkedToProductsModal"
            :option-set="selectedOptionSet"
            @close="closeOptionSetLinkedToProductsModal()"
        />

        <audit-log
            :value="showAuditLogModal"
            :entity-type="entityType"
            :entity-id="selectedSet?.id"
            :entity-created-date="selectedSet?.created_at"
            :display-name="selectedSet?.title"
            @close="onAuditClose()"
            @on-version-restored="onOptionSetVersionRestored"
        />

        <b-modal ref="setInUseModal" title="Set in use" ok-only>
            This set is being used by at least these products:

            <ul class="mt-3">
                <li v-for="product in productsUsingDeletedSet.filter((p) => !p.isDesign)" :key="product.id">
                    {{ product.title }}
                </li>
            </ul>

            <div v-if="productsUsingDeletedSet.filter((p) => p.isDesign).length">
                This set is being used by this design:

                <ul class="mt-3">
                    <li v-for="design in productsUsingDeletedSet.filter((p) => p.isDesign)" :key="design.id">
                        {{ design.id }}
                    </li>
                </ul>
            </div>

            Remove the set from these products to be able to delete it.
        </b-modal>

        <b-modal
            ref="optionSetsLinkedToProductsModal"
            :title="`${productsUsingDeletedSets.length} Option ${
                productsUsingDeletedSets.length === 1 ? 'Set' : 'Sets'
            } in use`"
            ok-only
            size="lg"
            centered
        >
            <div style="max-height: 60vh; overflow-y: auto" class="pr-2">
                <div v-for="set in productsUsingDeletedSets" :key="set.setId" class="mb-4">
                    <h5 class="mb-2">{{ set.setName }}</h5>

                    <div v-if="set.products.filter((p) => !p.isDesign).length">
                        <p>
                            This set is being used by the following product{{
                                set.products.filter((p) => !p.isDesign).length > 1 ? 's' : ''
                            }}:
                        </p>
                        <ul class="mt-2">
                            <li v-for="product in set.products.filter((p) => !p.isDesign)" :key="product.id">
                                {{ product.title }}
                            </li>
                        </ul>
                    </div>

                    <div v-if="set.products.filter((p) => p.isDesign).length" class="mt-3">
                        <p>
                            This set is being used by the following design{{
                                set.products.filter((p) => p.isDesign).length > 1 ? 's' : ''
                            }}:
                        </p>
                        <ul class="mt-2">
                            <li v-for="design in set.products.filter((p) => p.isDesign)" :key="design.id">
                                {{ design.id }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <p class="mt-3">
                Remove the set{{ productsUsingDeletedSets.length > 1 ? 's' : '' }} from these products to be able to
                delete {{ productsUsingDeletedSets.length > 1 ? 'them' : 'it' }}.
            </p>
        </b-modal>

        <b-modal ref="uploadFileModal" title="File to import" hide-footer>
            Choose .zip file with options to import:
            <div class="row justify-content-center my-2">
                <b-form-file
                    v-model="optionSetsZipFile"
                    accept=".zip"
                    :state="Boolean(optionSetsZipFile)"
                    placeholder="Choose file..."
                    class="col-8"
                />
                <c-button
                    class="ml-2 col-3"
                    variant="primary"
                    text="Import"
                    icon="fas fa-file-import"
                    @click="importOptionSets()"
                />
            </div>
            <c-button text="Cancel" variant="secondary" @click="closeUploadFileModal()" />
        </b-modal>
        <b-modal
            ref="templatePickerModal"
            size="lg"
            :title="setTaskIdentifier ? 'Creating option set' : 'Select a template'"
            scrollable
            @hidden="onTemplatePickerHidden"
        >
            <customily-products-list v-if="!setTaskIdentifier" @select="onTemplateSelect" />
            <set-progression v-if="setTaskIdentifier" :task-identifier="setTaskIdentifier" />
        </b-modal>
    </div>
</template>

<style lang="scss" scoped>
.id-cell {
    font-weight: 400;
    font-size: 12px;
    line-height: 100%;
    color: #7258e0;
}

.text-cell {
    font-weight: 400;
    font-size: 14px;
    line-height: 100%;
    color: #333333;
}

.table-icons {
    line-height: unset;
    opacity: 0;
    transition: 0.25s;
}

.table-row {
    &:active {
        background-color: #e6e6e6;
    }

    &:hover {
        background-color: #f0f1f2;

        .table-icons {
            opacity: 1;
            transition: 0.25s;
        }
    }
}

.export-row {
    display: flex;
    align-items: center;
    min-height: 35px;
    margin-left: 0.6rem;

    & > div > div > div {
        height: 14px;
        margin-left: 1.5rem;
        cursor: pointer;
    }
}

.action-btn-container {
    justify-content: space-between;
    margin-bottom: 1rem;
    margin-top: 1rem;

    .add-opt-set-btn {
        margin-left: 0;
    }
}

.center-icons-text {
    display: flex;
    align-items: center;
    margin-top: 3px;
}

.icons-on-action-col {
    margin-right: 0.25rem;
    margin-left: 0.25rem;
    padding: 0.375rem 0.75rem;
    border-radius: 0.25rem;
    transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out,
        box-shadow 0.15s ease-in-out;

    &:hover {
        color: #212529;
        background-color: #d0d1d6;
    }
}

::v-deep .custom-control:not(.ignore-custom-control) .custom-control-input:checked ~ .custom-control-label::before {
    color: #fff !important;
    border-color: #7258e0 !important;
    background-color: #7258e0 !important;
}

::v-deep .custom-control-label::after {
    cursor: pointer;
}

::v-deep .check-cell {
    justify-content: center !important;
    cursor: pointer;
    position: relative;
    top: -8px;

    & fieldset {
        margin: 0;
    }
}

::v-deep .check-col {
    justify-content: center !important;
    position: relative;
    top: 5px;

    & fieldset {
        margin: 0;
    }
}

.id-container-cell {
    width: fit-content;
}
</style>
